import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import Iconify from "@/components/iconify/iconify";
import { hatsApiFetch } from "@/api/hatsApi";
import { useLaddersContent } from "@/hooks/useCMSData";
import { useCurrentUser } from "@/hooks/useCurrentUser";

interface LadderResult {
  ladderId: string;
  registrationId: string;
  previousStatus: string;
  rusticiStatus?: string;
  action: "completed" | "none" | "error";
  xpAwarded?: number;
  newXp?: number;
  error?: string;
}

interface SyncResponse {
  climberId: string;
  ladders: LadderResult[];
  message?: string;
}

const statusLabel = (status?: string) => {
  if (!status) return "Unknown";
  if (status === "COMPLETED") return "Completed";
  if (status === "INCOMPLETE" || status === "IN_PROGRESS") return "In Progress";
  return "Not Started";
};

const StatusIcon = ({ status }: { status?: string }) => {
  if (status === "COMPLETED")
    return (
      <Iconify
        icon="mdi:check-circle"
        width={20}
        sx={{ color: "success.main" }}
      />
    );
  if (status === "INCOMPLETE" || status === "IN_PROGRESS")
    return (
      <Iconify
        icon="mdi:progress-clock"
        width={20}
        sx={{ color: "warning.main" }}
      />
    );
  return (
    <Iconify
      icon="mdi:circle-outline"
      width={20}
      sx={{ color: "text.disabled" }}
    />
  );
};

export const IbmSync = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SyncResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const currentUser = useCurrentUser();
  const { ladderContent } = useLaddersContent();

  const ladderNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const ladder of ladderContent) {
      map[ladder.id] = ladder.name;
    }
    return map;
  }, [ladderContent]);

  // Ladders with a Rustici registration that are already completed locally
  const completedLadders = useMemo(() => {
    const progress = currentUser.ladderProgress || [];
    return progress.filter(
      (lp) => lp.externalRegistrationId && lp.overallStatus === "COMPLETED",
    );
  }, [currentUser.ladderProgress]);

  const handleSync = async () => {
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const data = (await hatsApiFetch("/rustici/debug", {
        method: "POST",
      })) as SyncResponse;
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        IBM Course Data Synchronization
      </Typography>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="body1" color="text.secondary">
            Clicking the button below will synchronize your IBM lesson data with
            Hats &amp; Ladders. Any lessons you have completed in IBM will be
            updated here automatically.
          </Typography>
        </CardContent>
      </Card>

      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleSync}
          disabled={loading}
          startIcon={<Iconify icon="mdi:sync" width={20} />}
        >
          {loading ? "Syncing\u2026" : "Sync Now"}
        </Button>
      </Box>

      {loading && <LinearProgress sx={{ mb: 3, borderRadius: 1 }} />}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {completedLadders.length > 0 && (
        <Stack spacing={2} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Completed IBM Courses
          </Typography>
          {completedLadders.map((lp) => (
            <Card key={lp.ladderId} variant="outlined">
              <CardContent
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  py: 1.5,
                  "&:last-child": { pb: 1.5 },
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  noWrap
                  sx={{ minWidth: 0, flex: 1 }}
                >
                  {ladderNameMap[String(lp.ladderId)] ?? lp.ladderId}
                </Typography>
                <Chip
                  icon={<StatusIcon status="COMPLETED" />}
                  label="Completed"
                  size="small"
                  variant="filled"
                  color="success"
                  sx={{ ml: 2, flexShrink: 0 }}
                />
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {result && (
        <Stack spacing={2}>
          {result.ladders.length === 0 ? (
            <Alert severity="info">
              No IBM courses currently in progress. If you have started an IBM
              course, it may take a few minutes for data to become available.
            </Alert>
          ) : (
            result.ladders.map((ladder) => (
              <Card key={ladder.ladderId} variant="outlined">
                <CardContent
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    py: 1.5,
                    "&:last-child": { pb: 1.5 },
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600} noWrap>
                      {ladderNameMap[ladder.ladderId] ?? ladder.ladderId}
                    </Typography>

                    {ladder.action === "error" && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          mt: 0.5,
                        }}
                      >
                        <Iconify
                          icon="mdi:alert-circle-outline"
                          width={16}
                          sx={{ color: "error.main" }}
                        />
                        <Typography variant="caption" color="error">
                          Sync failed
                        </Typography>
                      </Box>
                    )}

                    {ladder.action === "completed" && ladder.xpAwarded && (
                      <Typography variant="caption" color="success.main">
                        +{ladder.xpAwarded} XP awarded
                      </Typography>
                    )}
                  </Box>

                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ ml: 2, flexShrink: 0 }}
                  >
                    {/* Before */}
                    <Chip
                      icon={<StatusIcon status={ladder.previousStatus} />}
                      label={statusLabel(ladder.previousStatus)}
                      size="small"
                      variant="outlined"
                      sx={{ minWidth: 110 }}
                    />

                    <Typography variant="body2" color="text.disabled">
                      &rarr;
                    </Typography>

                    {/* After */}
                    <Chip
                      icon={
                        <StatusIcon
                          status={ladder.rusticiStatus ?? ladder.previousStatus}
                        />
                      }
                      label={statusLabel(
                        ladder.rusticiStatus ?? ladder.previousStatus,
                      )}
                      size="small"
                      variant={
                        ladder.action === "completed" ? "filled" : "outlined"
                      }
                      color={
                        ladder.action === "completed" ? "success" : "default"
                      }
                      sx={{ minWidth: 110 }}
                    />
                  </Stack>
                </CardContent>
              </Card>
            ))
          )}
        </Stack>
      )}
    </Container>
  );
};
